/**
 * PlanetAtmosphereMaterial — a planet's atmosphere seen from orbit (shader
 * wave stage 4, 2026-09-09): Sean O'Neil's "Accurate Atmospheric Scattering"
 * (GPU Gems 2, ch. 16), the SkyFromSpace pass, evaluated per FRAGMENT.
 *
 * The outer sphere (planet radius × 1.025) is drawn BACK-faced (O'Neil's
 * `Cull Front`) and additive: a fragment is the far EXIT of the view ray, the
 * entry is the analytic near intersection, and the planet's own depth hides
 * every ray that would end in the ground — so only the LIMB integrates (the
 * blue halo around the disc). The single-scattering integral along that
 * chord uses O'Neil's optical-depth `scale` polynomial (Rayleigh and Mie
 * share it, the Mie phase is Henyey–Greenstein with g); Rayleigh colour =
 * 1/λ⁴ per channel, Mie a white forward lobe.
 *
 * 2026-09-09 ("a bright blob that takes up most of the planet's surface"):
 * the first cut drew FRONT faces (chord ≈ 0 for every fragment, but the
 * quadratic's b²−4c cancelled catastrophically in float32 at a 26 km radius
 * seen from megametres — the residual chord lit the whole disc). The
 * intersection is now the stable perpendicular-distance form in UNIT space
 * (everything divided by the outer radius) and the camera-inside case clamps
 * the entry at the camera.
 *
 * `oneilScaleCpu` and `rayleighPhaseCpu` / `miePhaseCpu` are the oracles.
 */

import * as THREE from 'three/webgpu';
import {
  Fn, uniform, float, vec3, vec4, positionLocal, cameraPosition, modelWorldMatrixInverse,
  normalize, dot, sqrt, max, exp, pow, Loop, length, saturate,
} from 'three/tsl';

export interface PlanetAtmosphereConfig {
  /** Planet radius in the mesh's local units (default 1, the outer sphere is 1.025). */
  innerRadius?: number;
  /** Atmosphere radius (default innerRadius × 1.025). */
  outerRadius?: number;
  /** Sun direction (world, unit) — a uniform the caller drives. */
  sunDirection?: THREE.Vector3;
  /** Rayleigh constant (default 0.0025). */
  kr?: number;
  /** Mie constant (default 0.0010). */
  km?: number;
  /** Sun brightness (default 20). */
  eSun?: number;
  /** Mie asymmetry g (default -0.95). */
  g?: number;
  /** Wavelengths (µm) for R, G, B (default 0.650, 0.570, 0.475). */
  wavelengths?: [number, number, number];
  /** Integration samples (default 5, O'Neil's). */
  samples?: number;
  /** Intensity multiplier on the result (default 1). */
  intensity?: number;
}

export interface PlanetAtmosphereUniforms {
  sunDirection: { value: THREE.Vector3 };
  intensity: { value: number };
}

/** O'Neil's optical-depth lookup polynomial for scale depth 0.25 (GPU Gems 2 §16.3). */
export function oneilScaleCpu(cos: number): number {
  const x = 1 - cos;
  return 0.25 * Math.exp(-0.00287 + x * (0.459 + x * (3.83 + x * (-6.80 + x * 5.25))));
}
export function rayleighPhaseCpu(cos: number): number { return 0.75 * (1 + cos * cos); }
export function miePhaseCpu(cos: number, g: number): number {
  const g2 = g * g;
  return 1.5 * ((1 - g2) / (2 + g2)) * (1 + cos * cos) / Math.pow(1 + g2 - 2 * g * cos, 1.5);
}

export function createPlanetAtmosphereMaterial(config?: PlanetAtmosphereConfig): { material: THREE.NodeMaterial; uniforms: PlanetAtmosphereUniforms } {
  const inner = config?.innerRadius ?? 1;
  const outer = config?.outerRadius ?? inner * 1.025;
  const kr = config?.kr ?? 0.0025, km = config?.km ?? 0.0010, eSun = config?.eSun ?? 20, g = config?.g ?? -0.95;
  const wl = config?.wavelengths ?? [0.650, 0.570, 0.475];
  const inv = [1 / Math.pow(wl[0], 4), 1 / Math.pow(wl[1], 4), 1 / Math.pow(wl[2], 4)];
  const samples = Math.max(2, Math.min(16, config?.samples ?? 5));
  const scaleDepth = 0.25;
  const uSun = uniform(config?.sunDirection?.clone() ?? new THREE.Vector3(1, 0, 0));
  const uIntensity = uniform(config?.intensity ?? 1);

  const material = new THREE.MeshBasicNodeMaterial();
  material.transparent = true;
  material.depthWrite = false;
  material.depthTest = true;
  material.side = THREE.BackSide; // the far exit; the planet's depth removes the rays that end in the ground
  material.blending = THREE.AdditiveBlending;
  material.fog = false;

  const scaleFn = (cos: ReturnType<typeof float>) => {
    const x = float(1).sub(cos);
    return float(scaleDepth).mul(exp(x.mul(x.mul(x.mul(x.mul(5.25).add(-6.80)).add(3.83)).add(0.459)).add(-0.00287)));
  };

  // UNIT space: every length below is divided by the outer radius so the
  // float32 arithmetic sees O(1) numbers whatever the planet's size.
  const innerU = inner / outer;
  const scaleHU = 1 / (1 - innerU);
  material.colorNode = Fn(() => {
    const camL = modelWorldMatrixInverse.mul(vec4(cameraPosition, 1)).xyz.div(outer);
    const sunL = normalize(modelWorldMatrixInverse.mul(vec4(uSun, 0)).xyz);
    const pos = positionLocal.div(outer);
    const ray = pos.sub(camL);
    const far = length(ray);
    const dir = ray.div(far);
    // Entry into the unit atmosphere along the view ray — the stable form:
    // tca = distance along the ray to the closest approach, d² the miss
    // distance², thc the half-chord; near = tca − thc, clamped at the camera.
    const tca = dot(camL, dir).negate();
    const d2 = dot(camL, camL).sub(tca.mul(tca));
    const thc = sqrt(max(float(0), float(1).sub(d2)));
    const near = max(float(0), tca.sub(thc));
    const start = camL.add(dir.mul(near));
    const segLen = max(float(0), far.sub(near));
    const startAngle = dot(dir, start);
    const startDepth = exp(float(-1).div(scaleDepth));
    const startOffset = startDepth.mul(scaleFn(startAngle));
    const sampleLen = segLen.div(samples);
    const scaledLen = sampleLen.mul(scaleHU);
    const sampleRay = dir.mul(sampleLen);
    const samplePoint = start.add(sampleRay.mul(0.5)).toVar();
    const front = vec3(0, 0, 0).toVar();
    const invWl = vec3(inv[0], inv[1], inv[2]);
    Loop(samples, () => {
      const h = length(samplePoint);
      const depth = exp(float(innerU).sub(h).mul(scaleHU / scaleDepth)); // O'Neil: exp(scaleOverScaleDepth · (innerRadius − height)), unit space
      const lightAngle = dot(sunL, samplePoint).div(h);
      const camAngle = dot(dir, samplePoint).div(h);
      const scatter = startOffset.add(depth.mul(scaleFn(lightAngle).sub(scaleFn(camAngle))));
      const atten = exp(scatter.negate().mul(invWl.mul(kr * 4 * Math.PI).add(km * 4 * Math.PI)));
      front.addAssign(atten.mul(depth.mul(scaledLen)));
      samplePoint.addAssign(sampleRay);
    });
    const cos = dot(sunL, dir.negate());
    const rayleigh = float(0.75).mul(float(1).add(cos.mul(cos)));
    const g2 = g * g;
    const mie = float(1.5 * ((1 - g2) / (2 + g2))).mul(float(1).add(cos.mul(cos))).div(pow(float(1 + g2).sub(cos.mul(2 * g)), 1.5));
    const colR = front.mul(invWl.mul(kr * eSun)).mul(rayleigh);
    const colM = front.mul(km * eSun).mul(mie);
    const col = colR.add(colM);
    // HDR-ish exposure so the limb never clips white.
    const exposed = float(1).sub(exp(col.mul(-1.0)));
    return vec4(saturate(exposed).mul(uIntensity), 1);
  })();

  return { material: material as THREE.NodeMaterial, uniforms: { sunDirection: uSun as unknown as { value: THREE.Vector3 }, intensity: uIntensity as unknown as { value: number } } };
}
